import React, { useCallback, useEffect, useContext, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";

// Services
import AuthenticationService from "../../../services/AuthenticationService";
import { NotificationsContext } from "../../Notifications";
import getRoutePath from "../../../routes/getRoutePath";
import apiRequest from "../../../services/apiRequest";
import CountryContext from "../../../CountryContext";
import activeCountries from "../../../activeCountries";
import { CITY_REGEX, ZIP_CODE_REGEX } from "../../../utils/regex";

// Ui
import { CheckIcon, XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import { PageHeading, renderSectionEditButton } from "../../Container";
import UpdatePassword from "../../forms/UpdatePassword";
import Input from "../../Input";
import Button from "../../Button";
import Modal from "../../Modal";
import DeleteIcon from "@material-ui/icons/Delete";
import RadioGroup from "../../RadioGroup";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { validateEmail } from "@utils/validate-email";
import { ProfileAccountUserSection } from "@feature/profile-account-user-section";
import Select from "ui-components/Select";
import EditRoleComponent from "./EditRoleComponent";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {FixRoleWarningModal} from "../../../feature/profile-account-user-section/FixRoleWarningModal";
import EditCityAndZipCodeModal from "./EditCityAndZipCodeModal";

function Profile() {
  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const [loading, setLoading] = useState(false);
  const userData = AuthenticationService.getUserData();
    const apiRequestCountry = useCountryAxiosInstance();

  // const countryOptions = activeCountries.map((i) => ({
  //   value: i.code,
  //   name: i.name,
  // }));

  const [externalRoles, setExternalRoles] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { push } = useContext(NotificationsContext);

  const [updatingKey, setUpdatingKey] = useState(undefined);
  const [updatingValue, setUpdatingValue] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [churnReason, setChurnReason] = useState();
  const [updateRole, setUpdateRole] = useState(false);
  const [showFixRoleWarningModal, setShowFixRoleWarningModal] = useState(userData.show_fix_role_warning);
  const [showEditCityAndZipCodeModal, setShowEditCityAndZipCodeModal] = useState(false);

  const refreshToken = async () => {
    const {access_token, user_data} = await apiRequest.get('/users/refresh_token')
    AuthenticationService.setAuthToken(access_token, user_data)
  }
  useEffect(() => {

    refreshToken()
  }, [])

  const noMoreShowFixRoleWarning = async () => {
    const userProfileData = await apiRequest.get('/users/' + userData.id);
    const response = await apiRequest.post(`/users/${userProfileData.id}`, {
      ...userProfileData,
      show_fix_role_warning: false
    });
    await refreshToken();
    // window.location.reload();
    setShowFixRoleWarningModal(false);
  }

  useEffect(async () => {
    const fetchUserTypes = async () => {
      setLoading(true);
      try {
        const [allUserTypes] = await Promise.all([
          apiRequestCountry.get(`/user_types/all`, { publicRequest: true }),
        ]);
        setExternalRoles(allUserTypes);
      } catch (error) {
        console.log("errore", error);
      } finally {
        setLoading(false);
      }
    };
    await fetchUserTypes();
    // eslint-disable-next-line
  }, []);

  const makeEditable = (key) => {
    setUpdatingKey(key);
    setUpdatingValue(userData[key]);
  };

  const closeUpdate = () => {
    setUpdatingKey(undefined);
    setUpdatingValue("");
  };

  const saveUpdate = () => {

    if (
      (updatingKey === "email" && !validateEmail(updatingValue.trim())) ||
      (updatingKey !== "city_and_zip_code" && updatingValue.trim() === "")
    ) {
      push({
        title: intl.formatMessage({ id: "invalid_field" }),
        type: "error",
      });
    } else {
      apiRequest
        .get(`/users/${userData.id}`)
        .then((res) => {
          const data = updatingKey === 'city_and_zip_code' ?
            { ...res, city: updatingValue.city, zip_code: updatingValue.zip_code } :
            { ...res, [updatingKey]: updatingValue };
          apiRequest.post(`/users/${userData.id}`, data).then((data) => {
            if (updatingKey === "email") {
              const {token_and_userdata} = data
              AuthenticationService.setAuthToken(
                token_and_userdata.access_token,
                token_and_userdata.user_data
              );
              push({
                title: intl.formatMessage({
                  id: "user_updated_successfully",
                }),
                type: "success",
              });
            } else {
              apiRequest.get("/users/refresh_token").then((res) => {
                AuthenticationService.setAuthToken(
                  res.access_token,
                  res.user_data
                );
                push({
                  title: intl.formatMessage({
                    id: "user_updated_successfully",
                  }),
                  type: "success",
                });
              }).catch(() => {
                push({
                  title: intl.formatMessage({ id: "server_error" }),
                  type: "error",
                });
              });
            }
          });
        })
        .catch(() => {
          push({
            title: intl.formatMessage({ id: "server_error" }),
            type: "error",
          });
        })
        .finally(() => {
          setUpdatingKey(undefined);
          setUpdatingValue("");
        });
    }
  };

  const deleteAccount = () => {
    setLoading(true);
    apiRequest
      .delete(`/users/${userData.id}?churn_reason=${churnReason}`)
      .then(() => {
        push({
          title: intl.formatMessage({ id: "user_deleted_successfully" }),
          type: "success",
        });
        setLoading(false);
        AuthenticationService.logout();
        window.location = getRoutePath("/login");
      })
      .catch(() => {
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
      });
  };

  const sectorOptions = {
    user_type_design_studio: [
      " ",
      "architecture",
      "engineering",
      "interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_freelance: [
      " ",
      "architecture",
      "engineering",
      "interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "structural_reinforcement",
      "waterproofing",
      "vertical_works",
      "construction",
      "civil_works_specialist",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
  };

  const qualificationOptions = {
    user_type_design_studio: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "decoration/interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "applicator",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_resale_tintometer: [" ", "owner", "other"].map((i) => ({
      value: i,
      name: intl.formatMessage({ id: i }),
    })),
    user_type_resale: [" ", "owner", "other"].map((i) => ({
      value: i,
      name: intl.formatMessage({ id: i }),
    })),
  };

  const userKeys = [
    {
      visible: true,
      key: "role_type",
      textKey: "user_type",
      editable: !["resale", "resale_tintometer", "contact_user"].includes(
        userData.role_type
      ),
      edit_type: "role",
      customOnClick: () => {
        setUpdateRole(true);
      },
    },
    {
      visible: true,
      key: "firstname",
      textKey: "firstname",
      editable: true,
      edit_type: "input",
    },
    {
      visible: true,
      key: "lastname",
      textKey: "lastname",
      editable: true,
      edit_type: "input",
    },
    {
      visible: true,
      key: "email",
      textKey: "email",
      editable: true,
      edit_type: "input",
    },
    // {
    //   visible: !["resale", "resale_tintometer"].includes(userData.role_type),
    //   key: "countries",
    //   textKey: "country",
    //   editable: false,
    //   edit_type: "select",
    //   options: countryOptions,
    // },
    // {
    //   visible: !["resale", "resale_tintometer"].includes(
    //     userData.role_type
    //   ) && !userData.flg_internal,
    //   key: "city",
    //   textKey: "city",
    //   editable: true,
    //   edit_type: "input",
    //   validation: (value) => CITY_REGEX.test(value),
    // },
    // {
    //   visible: !["resale", "resale_tintometer"].includes(
    //     userData.role_type
    //   ) && !userData.flg_internal,
    //   key: "zip_code",
    //   textKey: "zip_code",
    //   editable: true,
    //   edit_type: "input",
    //   validation: (value) => ZIP_CODE_REGEX.test(value),
    // },
    {
      visible: !["resale", "resale_tintometer"].includes(
        userData.role_type
      ) && !userData.flg_internal,
      key: "city_and_zip_code",
      textKey: intl.formatMessage({id: "city"}) + ' - ' + intl.formatMessage({id: "zip_code"}),
      editable: true,
      edit_type: "city_and_zip_code",
      customOnClick: () => {
        setUpdatingKey('city_and_zip_code');
        setUpdatingValue({city: userData.city, zip_code: userData.zip_code});
        setShowEditCityAndZipCodeModal(true);
      }
    },
    {
      visible: !["contact_user", "freelance"].includes(
        userData.role_type
      ) && !userData.flg_internal,
      key: "company",
      textKey: "company_name",
      editable: true,
      edit_type: "input",
    },
    {
      visible: !["contact_user"].includes(
        userData.role_type
      ) && !userData.flg_internal,
      key: "vat",
      textKey: "vat",
      editable: true,
      edit_type: "input",
    },
    {
      visible: !["freelance"].includes(
        userData.role_type
      ) && !userData.flg_internal,
      key: "qualification",
      textKey: "qualification",
      editable: true,
      edit_type: "select",
      options: qualificationOptions["user_type_" + (userData.role_type === 'contact_user' ? userData.account_user_role_type : userData.role_type)],
    },
    {
      visible: !userData.flg_internal && !["contact_user"].includes(
        userData.role_type
        ),
      key: "stakeholder",
      textKey: "stakeholder",
      editable: true,
      edit_type: "select",
      options: sectorOptions["user_type_" + (userData.role_type === 'contact_user'? userData.account_user_role_type : userData.role_type)], // Condizione is not resale?
    },
    //{ key: "", textKey: "", editable: true },
  ];

  const GetEditComponent = ({ userKey }) => {
    const { edit_type, key, options } = userKey;

    switch (edit_type) {
      case "select":
        return (
          <Select
            value={updatingValue}
            onChange={(e) => {
              setUpdatingValue(e.target.value);
            }}
            options={options}
          />
        );
      case "input":
        return (
          <Input
            value={updatingValue}
            onChange={(e) => setUpdatingValue(e.target.value)}
            type={key === "email" ? "email" : "text"}
            errorMessage={
              key === "email"
                ? intl.formatMessage({ id: "enter_email" })
                : intl.formatMessage({ id: "required_field" })
            }
            validation={userKey.validation}
            required
          />
        );
      default:
        return <></>;
    }
  };

  const handleChangePassword = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !password ||
        !newPassword ||
        !confirmPassword ||
        confirmPassword !== newPassword
      ) {
        return;
      }

      const data = {
        email: userData.email,
        password: password,
        new_password: newPassword,
        country: country.code,
      };
      try {
        setLoading(true);
        await apiRequest.post("/users/password/reset", data);
        setOpenPassword(!openPassword);
        push({
          title: intl.formatMessage({ id: "password_updated_successfully" }),
          type: "success",
        });
      } catch (error) {
        console.log(error);
        push({
          title: intl.formatMessage({ id: "access_denied" }),
          type: "error",
        });
      } finally {
        setLoading(false);
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    },
    [push, newPassword, userData, password, openPassword, confirmPassword, intl] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <PageHeading title={intl.formatMessage({ id: "my_profile" })} />

      <div className="w-full flex justify-between items-baseline my-4">
        <h1 className="text-lg mb-2">
          {intl.formatMessage({
            id: "profile_subtitle",
            defaultMessage: "Modifica le informazioni personali",
          })}
        </h1>
        <div className="flex flex-row gap-2">
          {!userData.flg_internal && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenPassword(!openPassword)}
            >
              <FormattedMessage id="edit_password" />
            </Button>
          )}

          <Button
            styleType="secondary"
            onClick={() => {
              AuthenticationService.logout();
              window.location = getRoutePath("/login");
            }}
          >
            Logout
          </Button>
          {!userData.flg_internal && (
              <Button
                styleType="danger"
                className=" hover:text-gray-200"
                onClick={() => setShowDelete(!showDelete)}
                Icon={DeleteIcon}
              >{intl.formatMessage({ id: "delete_account" })}</Button>
          )}
        </div>
      </div>
      <div className="border border-gray-600 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-600">
            {userKeys
              .filter((el) => el.visible)
              .map((userKey) => {
                let {
                  key,
                  textKey,
                  editable,
                  customOnClick ,
                } = userKey;

                return (
                  <div
                    key={key}
                    className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                  >
                    <dt className="text-sm font-medium text-gray-500">
                      {intl.formatMessage({ id: textKey })}
                    </dt>
                    <dd className="mt-1 uppercase font-bold text-sm text-black-500 sm:mt-0 sm:col-span-2">
                      <div className="flex flex-row w-full">
                        {editable ? (
                          updatingKey === key ? (
                            <>
                              <div className="mr-5 flex flex-row -mt-2 -mb-2">
                                {renderSectionEditButton(
                                  CheckIcon,
                                  "save_edits",
                                  intl,
                                  saveUpdate
                                )}
                                {renderSectionEditButton(
                                  XMarkIcon,
                                  "cancel",
                                  intl,
                                  closeUpdate
                                )}
                              </div>
                              <div className="w-4/5 -mt-3 -mb-6">
                                {GetEditComponent({ userKey })}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="mr-5 -mt-2 -mb-2">
                                {renderSectionEditButton(
                                  PencilIcon,
                                  "edit",
                                  intl,
                                  customOnClick || (() => makeEditable(key))
                                )}
                              </div>
                              <div>{
                                ['stakeholder', 'qualification', 'role_type'].includes(key) ?
                                  userData[key] && intl.formatMessage({id: userData[key]}) :
                                  key === 'city_and_zip_code' ?
                                    userData['city'] + ' - ' + userData['zip_code']:
                                userData[key] && userData[key]
                              }</div>
                            </>
                          )
                        ) : (
                          <div>
                            {
                              key === 'role_type' && userData[key] === 'contact_user' ?
                                intl.formatMessage({id: userData['account_user_role_type']}) :
                                userData[key] &&
                                intl.formatMessage({id: userData[key]})
                            }
                          </div>
                        )}
                      </div>
                    </dd>
                  </div>
                );
              })}
          </dl>
        </div>
      </div>
      <div>
        <ProfileAccountUserSection
        />
      </div>


      {Object.entries(userData.ref_users).length && (
        <div className="mb-20">
          <h1 className="text-lg mt-5 mb-2">Riferimenti aziendali</h1>
          <div className="border border-gray-600 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-600">
                {Object.entries(userData.ref_users).map(([key, refUser]) => (
                  <div
                    key={key}
                    className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                  >
                    <dt className="text-sm font-medium text-gray-500">
                      {intl.formatMessage({ id: key })}
                    </dt>
                    <dd className="mt-1 uppercase font-bold text-sm text-black-500 sm:mt-0 sm:col-span-2">
                      {Object.entries(refUser).map(([key, user]) => (
                        <div key={key}>
                          <span>{user.name}</span>
                          <span className="ml-5 text-xs">
                            <a
                              className="text-am-500"
                              href={"mailto:" + user.email}
                            >
                              {user.email}
                            </a>
                          </span>
                        </div>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* MODALS */}
      {showDelete ? (
        <Modal onExit={() => setShowDelete(!showDelete)}>
          <div className="m-8">
            <h3 className="text-center text-2xl font-extrabold text-black-500">
              <FormattedMessage id="delete_account" />
            </h3>
          </div>
          <div className="ml-2">
            <RadioGroup
              id="churn-reason"
              description={intl.formatMessage({ id: "select_churn_reason" })}
              options={[
                {
                  value: "churn_reason_1",
                  name: intl.formatMessage({ id: "churn_reason_1" }),
                },
                {
                  value: "churn_reason_2",
                  name: intl.formatMessage({ id: "churn_reason_2" }),
                },
                {
                  value: "churn_reason_3",
                  name: intl.formatMessage({ id: "churn_reason_3" }),
                },
              ]}
              currentValue={churnReason}
              onChange={(value) => {
                setChurnReason(value);
              }}
            />
          </div>
          <div className="italic my-5 text-black-700 flex flex-row justify-center">
            <ExclamationCircleIcon className="w-5 h-5 mr-2" />
            {intl.formatMessage({ id: "churn_disclaimer" })}
          </div>
          <div className="mt-4 flex items-end justify-end">
            <Button
              submitting={loading}
              disabled={!churnReason}
              onClick={deleteAccount}
            >
              <FormattedMessage id="confirm" />
            </Button>
          </div>
        </Modal>
      ) : null}
      {openPassword ? (
        <Modal onExit={() => setOpenPassword(!openPassword)}>
          <div className="m-8">
            <h3 className="text-center text-2xl font-extrabold text-black-500">
              <FormattedMessage id="edit_password" />
            </h3>
          </div>

          <form onSubmit={handleChangePassword}>
            <Input
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              label={intl.formatMessage({id: "current_password", defaultMessage: "Password attuale"})}
              type="password"
              errorMessage={intl.formatMessage({
                id: "enter_actual_password",
              })}
              required
            />
            <UpdatePassword
              setPassword={setNewPassword}
              setConfirmPassword={setConfirmPassword}
              password={newPassword}
              confirmPassword={confirmPassword}
              checkUserOldPasswords
            />
            <div className="flex items-end justify-end">
              <Button
                type="submit"
                submitting={loading}
                disabled={!password || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              >
                <FormattedMessage id="send" />
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
      {
        showFixRoleWarningModal && (
          <FixRoleWarningModal
            onConfirm={()=>{
              setUpdateRole(true);
              setShowFixRoleWarningModal(false);
            }}
            onCancel={
              async ()=> {
                await noMoreShowFixRoleWarning()
                setShowFixRoleWarningModal(false);
              }
            }
          />
        )
      }
      {updateRole && (
        <Modal
          large
          onExit={async () => {
            setUpdateRole(false);
            await noMoreShowFixRoleWarning()
          }}
        >
          <EditRoleComponent
            userData={userData}
            userKeys={userKeys}
            onSubmit={() => {
              setUpdateRole(false);
            }}
            externalRoles={externalRoles}
          />
        </Modal>
      )}
      {
        showEditCityAndZipCodeModal && (
          <EditCityAndZipCodeModal
            updatingValue={updatingValue}
            setUpdatingValue={setUpdatingValue}
            onConfirm={ async ()=>{
              saveUpdate();
              setShowEditCityAndZipCodeModal(false);
            }}
            onClose={()=>{
              setUpdatingKey(undefined);
              setUpdatingValue("");
              setShowEditCityAndZipCodeModal(false);
            }}
          />
        )
      }
    </>
  );
}

export default Profile;
